import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  Users, 
  Edit,
  Save,
  Upload,
  Link,
  FileText,
  Globe,
  Phone,
  Mail,
  MapPin,
  Image,
  Loader2,
  CheckCircle,
  Shield,
  Briefcase,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Share2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { sectorsApi, companyApi } from '../lib/api';

export const Clients = () => {
  const { company, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [companyData, setCompanyData] = useState({
    name: '',
    sector: '',
    profile: {
      logoUrl: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'España',
      phone: '',
      email: '',
      website: ''
    },
    legalLinks: {
      privacyPolicy: '',
      termsOfService: '',
      legalNotice: '',
      cookiePolicy: ''
    },
    socialLinks: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: ''
    }
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch company data
      const companyRes = await companyApi.getMyCompany();
      const companyInfo = companyRes.data.company;
      
      setCompanyData({
        name: companyInfo.name || '',
        sector: companyInfo.sector || '',
        profile: {
          logoUrl: companyInfo.profile?.logoUrl || '',
          address: companyInfo.profile?.address || '',
          city: companyInfo.profile?.city || '',
          postalCode: companyInfo.profile?.postalCode || '',
          country: companyInfo.profile?.country || 'España',
          phone: companyInfo.profile?.phone || '',
          email: companyInfo.profile?.email || '',
          website: companyInfo.profile?.website || ''
        },
        legalLinks: {
          privacyPolicy: companyInfo.legalLinks?.privacyPolicy || '',
          termsOfService: companyInfo.legalLinks?.termsOfService || '',
          legalNotice: companyInfo.legalLinks?.legalNotice || '',
          cookiePolicy: companyInfo.legalLinks?.cookiePolicy || ''
        },
        socialLinks: {
          facebook: companyInfo.socialLinks?.facebook || '',
          instagram: companyInfo.socialLinks?.instagram || '',
          linkedin: companyInfo.socialLinks?.linkedin || '',
          twitter: companyInfo.socialLinks?.twitter || '',
          youtube: companyInfo.socialLinks?.youtube || ''
        }
      });
      
      // Fetch sectors
      const sectorsRes = await sectorsApi.list();
      setSectors(sectorsRes.data.sectors || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyApi.updateMyCompany(companyData);
      toast.success('Datos de empresa actualizados correctamente');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const updateProfile = (key, value) => {
    setCompanyData(prev => ({
      ...prev,
      profile: { ...prev.profile, [key]: value }
    }));
  };

  const updateLegalLinks = (key, value) => {
    setCompanyData(prev => ({
      ...prev,
      legalLinks: { ...prev.legalLinks, [key]: value }
    }));
  };

  const updateSocialLinks = (key, value) => {
    setCompanyData(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Mi Empresa</h1>
          <p className="text-slate-500 mt-1">
            Configura los datos de tu empresa para personalizar automáticamente tus emails
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-company-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="mb-8 border-indigo-200 bg-indigo-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-medium text-indigo-900">Personalización automática de emails</p>
              <p className="text-sm text-indigo-700 mt-1">
                Los datos que configures aquí se usarán automáticamente en el encabezado y pie de página de todos tus emails.
                El logotipo, nombre de empresa y enlaces legales aparecerán en cada campaña que envíes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList>
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Datos de Empresa
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Enlaces Legales
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Redes Sociales
          </TabsTrigger>
        </TabsList>

        {/* Company Data Tab */}
        <TabsContent value="company" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Logo & Basic Info */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logotipo
                </CardTitle>
                <CardDescription>
                  Se mostrará en el encabezado de tus emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                  {companyData.profile.logoUrl ? (
                    <img 
                      src={companyData.profile.logoUrl} 
                      alt="Logo" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Sin logotipo</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="logoUrl">URL del logotipo</Label>
                  <Input
                    id="logoUrl"
                    value={companyData.profile.logoUrl}
                    onChange={(e) => updateProfile('logoUrl', e.target.value)}
                    placeholder="https://ejemplo.com/logo.png"
                    className="mt-2"
                    data-testid="logo-url-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Recomendado: PNG transparente, max 200x60px
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Company Details */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Información de la Empresa
                </CardTitle>
                <CardDescription>
                  Estos datos aparecerán en el pie de página de tus emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="companyName">Nombre de la empresa</Label>
                    <Input
                      id="companyName"
                      value={companyData.name}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Mi Empresa S.L."
                      className="mt-2"
                      data-testid="company-name-input"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label htmlFor="sector">Sector</Label>
                    <Select 
                      value={companyData.sector} 
                      onValueChange={(v) => setCompanyData(prev => ({ ...prev, sector: v }))}
                    >
                      <SelectTrigger className="mt-2" data-testid="sector-select">
                        <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Selecciona un sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map(sector => (
                          <SelectItem key={sector.id} value={sector.name}>
                            {sector.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="otros">Otros</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Las plantillas se filtrarán según tu sector
                    </p>
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="address">Dirección</Label>
                    <Input
                      id="address"
                      value={companyData.profile.address}
                      onChange={(e) => updateProfile('address', e.target.value)}
                      placeholder="Calle Ejemplo 123, 1º"
                      className="mt-2"
                      data-testid="address-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">Ciudad</Label>
                    <Input
                      id="city"
                      value={companyData.profile.city}
                      onChange={(e) => updateProfile('city', e.target.value)}
                      placeholder="Madrid"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="postalCode">Código Postal</Label>
                    <Input
                      id="postalCode"
                      value={companyData.profile.postalCode}
                      onChange={(e) => updateProfile('postalCode', e.target.value)}
                      placeholder="28001"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        value={companyData.profile.phone}
                        onChange={(e) => updateProfile('phone', e.target.value)}
                        placeholder="+34 912 345 678"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email de contacto</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={companyData.profile.email}
                        onChange={(e) => updateProfile('email', e.target.value)}
                        placeholder="info@empresa.com"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="website">Sitio web</Label>
                    <div className="relative mt-2">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="website"
                        value={companyData.profile.website}
                        onChange={(e) => updateProfile('website', e.target.value)}
                        placeholder="https://www.miempresa.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Legal Links Tab */}
        <TabsContent value="legal" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Enlaces Legales
              </CardTitle>
              <CardDescription>
                Estos enlaces aparecerán en el pie de página de todos tus emails para cumplir con la normativa RGPD
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="privacyPolicy" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Política de Privacidad
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="privacyPolicy"
                      value={companyData.legalLinks.privacyPolicy}
                      onChange={(e) => updateLegalLinks('privacyPolicy', e.target.value)}
                      placeholder="https://miempresa.com/privacidad"
                      className="pl-10"
                      data-testid="privacy-policy-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="termsOfService" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Términos y Condiciones
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="termsOfService"
                      value={companyData.legalLinks.termsOfService}
                      onChange={(e) => updateLegalLinks('termsOfService', e.target.value)}
                      placeholder="https://miempresa.com/terminos"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="legalNotice" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Aviso Legal
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="legalNotice"
                      value={companyData.legalLinks.legalNotice}
                      onChange={(e) => updateLegalLinks('legalNotice', e.target.value)}
                      placeholder="https://miempresa.com/aviso-legal"
                      className="pl-10"
                      data-testid="legal-notice-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cookiePolicy" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Política de Cookies
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="cookiePolicy"
                      value={companyData.legalLinks.cookiePolicy}
                      onChange={(e) => updateLegalLinks('cookiePolicy', e.target.value)}
                      placeholder="https://miempresa.com/cookies"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border">
                <h4 className="font-medium text-slate-900 mb-2">Vista previa del pie de página</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p><strong>{companyData.name || 'Tu Empresa'}</strong></p>
                  <p>{companyData.profile.address || 'Dirección'}, {companyData.profile.city || 'Ciudad'} {companyData.profile.postalCode}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-indigo-600">
                    {companyData.legalLinks.privacyPolicy && <span>Privacidad</span>}
                    {companyData.legalLinks.termsOfService && <span>Términos</span>}
                    {companyData.legalLinks.legalNotice && <span>Aviso Legal</span>}
                    {companyData.legalLinks.cookiePolicy && <span>Cookies</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Links Tab */}
        <TabsContent value="social" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Redes Sociales
              </CardTitle>
              <CardDescription>
                Añade los enlaces de tus redes sociales. Aparecerán como iconos en el pie de página de tus emails.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="facebook" className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    Facebook
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="facebook"
                      value={companyData.socialLinks.facebook}
                      onChange={(e) => updateSocialLinks('facebook', e.target.value)}
                      placeholder="https://facebook.com/tuempresa"
                      className="pl-10"
                      data-testid="facebook-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="instagram" className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-600" />
                    Instagram
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="instagram"
                      value={companyData.socialLinks.instagram}
                      onChange={(e) => updateSocialLinks('instagram', e.target.value)}
                      placeholder="https://instagram.com/tuempresa"
                      className="pl-10"
                      data-testid="instagram-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="linkedin" className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-700" />
                    LinkedIn
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="linkedin"
                      value={companyData.socialLinks.linkedin}
                      onChange={(e) => updateSocialLinks('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/company/tuempresa"
                      className="pl-10"
                      data-testid="linkedin-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="twitter" className="flex items-center gap-2">
                    <Twitter className="w-4 h-4 text-sky-500" />
                    Twitter / X
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="twitter"
                      value={companyData.socialLinks.twitter}
                      onChange={(e) => updateSocialLinks('twitter', e.target.value)}
                      placeholder="https://twitter.com/tuempresa"
                      className="pl-10"
                      data-testid="twitter-input"
                    />
                  </div>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <Label htmlFor="youtube" className="flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-600" />
                    YouTube
                  </Label>
                  <div className="relative mt-2">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="youtube"
                      value={companyData.socialLinks.youtube}
                      onChange={(e) => updateSocialLinks('youtube', e.target.value)}
                      placeholder="https://youtube.com/@tuempresa"
                      className="pl-10"
                      data-testid="youtube-input"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border">
                <h4 className="font-medium text-slate-900 mb-2">Vista previa de iconos sociales</h4>
                <div className="flex items-center gap-4 mt-2">
                  {companyData.socialLinks.facebook && (
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <Facebook className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {companyData.socialLinks.instagram && (
                    <div className="w-8 h-8 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Instagram className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {companyData.socialLinks.linkedin && (
                    <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                      <Linkedin className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {companyData.socialLinks.twitter && (
                    <div className="w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center">
                      <Twitter className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {companyData.socialLinks.youtube && (
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                      <Youtube className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {!companyData.socialLinks.facebook && !companyData.socialLinks.instagram && 
                   !companyData.socialLinks.linkedin && !companyData.socialLinks.twitter && 
                   !companyData.socialLinks.youtube && (
                    <p className="text-slate-500 text-sm">Añade al menos una red social para ver la vista previa</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Clients;
